import type { DropdownProps } from 'antd';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { usePreventDoubleClick } from '../../hooks';
import { type ClickDropdownPlacement, getClickDropdownPlacement } from '../../utils';

type UsePositionAtClickProperties = {
  menuWidth: number;
  onOpenChange?: DropdownProps['onOpenChange'];
  open?: DropdownProps['open'];
};

type UsePositionAtClickReturn = {
  handleOpenChange: NonNullable<DropdownProps['onOpenChange']>;
  isOpen: boolean;
  placement: ClickDropdownPlacement;
  setTriggerRef: (node: HTMLElement | null) => void;
};

export const usePositionAtClick = ({
  menuWidth,
  onOpenChange,
  open: isOpenProperty,
}: UsePositionAtClickProperties): UsePositionAtClickReturn => {
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const [clickPlacement, setClickPlacement] = useState<ClickDropdownPlacement>('bottomLeft');
  const [triggerElement, setTriggerElement] = useState<HTMLElement | null>(null);
  const isOpenReference = useRef(false);
  const pendingClickPlacementReference = useRef<ClickDropdownPlacement>('bottomLeft');

  const isControlled = isOpenProperty !== undefined;
  const isOpen = isControlled ? isOpenProperty : isOpenInternal;

  const setTriggerRef = useCallback((node: HTMLElement | null) => {
    setTriggerElement(node);
  }, []);

  useLayoutEffect(() => {
    isOpenReference.current = isOpen;
  }, [isOpen]);

  const handleMouseDown = useCallback(
    (event: MouseEvent) => {
      if (isOpenReference.current) {
        return;
      }

      pendingClickPlacementReference.current = getClickDropdownPlacement(event.clientX, menuWidth);
    },
    [menuWidth],
  );

  usePreventDoubleClick(triggerElement, { onMouseDown: handleMouseDown });

  const handleOpenChange = useCallback<NonNullable<DropdownProps['onOpenChange']>>(
    (nextIsOpen, info) => {
      isOpenReference.current = nextIsOpen;

      if (nextIsOpen) {
        setClickPlacement(pendingClickPlacementReference.current);
      }

      if (!isControlled) {
        setIsOpenInternal(nextIsOpen);
      }

      onOpenChange?.(nextIsOpen, info);
    },
    [isControlled, onOpenChange],
  );

  return {
    handleOpenChange,
    isOpen,
    placement: clickPlacement,
    setTriggerRef,
  };
};
